import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getTournamentById, getTournamentBySlug } from '../../database/tournaments';

import AnonymousPlayerGuessingGame from './TournamentGuessing/AnonymousPlayerGuessingGame';
import ExternalTournamentView from './ExternalTournamentView';
import TeamTournamentView from './TeamTournamentView';
import BracketTournamentView from './BracketTournamentView';
import BracketExternalTournamentView from './BracketExternalTournamentView';
import GroupsFinalTournamentView from './GroupsFinalTournamentView';
import LeaderboardTournamentView from './LeaderboardTournamentView';
import MappingTournamentView from './MappingTournamentView';
import RulesOnlyTournamentView from './RulesOnlyTournamentView';
import TeamCumulativeStandingsView from './TeamCumulativeStandingsView';
import TournamentHeader from './tournament/TournamentHeader';
import TournamentSupportSection from './tournament/TournamentSupportSection';
import StagedTournamentView from './stages/StagedTournamentView';
import SEO from '../SEO';
import { absoluteUrl, truncate } from '../../lib/seo';
import { isSectionVisible } from '../../lib/stages';

const toIsoDate = (v) => {
  if (!v) return undefined;
  if (typeof v.toDate === 'function') return v.toDate().toISOString();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

const TournamentDetails = () => {
  // The route param is the slug; legacy `/tournaments/<id>` URLs still resolve
  // by document ID and redirect to the slug when one exists.
  const { slug: routeKey } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [rounds, setRounds] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        let data = await getTournamentBySlug(routeKey);
        if (!data) {
          // Fall back to treating the param as a legacy document ID.
          const byId = await getTournamentById(routeKey);
          if (byId?.slug && byId.slug !== routeKey) {
            if (!cancelled) navigate(`/tournaments/${byId.slug}`, { replace: true });
            return;
          }
          data = byId;
        }
        if (!data) {
          if (!cancelled) setTournament(null);
          return;
        }
        if (!cancelled) setTournament(data);

        const id = data.id;
        const playersSnap = await getDocs(collection(db, 'tournaments', id, 'players'));
        if (!cancelled) setPlayers(playersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

        if (data.format === 'team') {
          const teamsSnap = await getDocs(
            query(collection(db, 'tournaments', id, 'teams'), orderBy('totalPoints', 'desc'))
          );
          const teamsData = teamsSnap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            members: d.data().members || [],
          }));
          if (!cancelled) setTeams(teamsData);

          const roundsSnap = await getDocs(
            query(collection(db, 'tournaments', id, 'rounds'), orderBy('date', 'desc'))
          );
          const roundsData = roundsSnap.docs
            .map((d) => ({ id: d.id, ...d.data(), results: d.data().results || [] }))
            .sort((a, b) => a.position - b.position);
          if (!cancelled) setRounds(roundsData);
        } else if (data.format === 'mapping' || data.format === 'team-cumulative') {
          const teamsSnap = await getDocs(
            query(collection(db, 'tournaments', id, 'teams'), orderBy('name'))
          );
          const teamsData = teamsSnap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            members: d.data().members || [],
          }));
          if (!cancelled) setTeams(teamsData);
        }
      } catch (err) {
        console.error('Error fetching tournament data:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [routeKey, navigate]);

  if (loading) {
    return (
      <div className="mx-auto flex max-w-screen-2xl items-center justify-center px-6 py-24">
        <div className="space-y-4">
          <div className="h-10 w-56 animate-pulse rounded-md bg-muted" />
          <div className="h-4 w-40 animate-pulse rounded-md bg-muted" />
          <div className="h-64 w-[40rem] max-w-full animate-pulse rounded-md bg-muted/60" />
        </div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="mx-auto max-w-screen-lg px-6 py-24 text-center">
        <SEO title="Tournament not found" noindex canonicalPath={`/tournaments/${routeKey}`} />
        <h1 className="font-display text-3xl font-semibold">Tournament not found</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          The tournament you're looking for doesn't exist or was removed.
        </p>
      </div>
    );
  }

  const tournamentId = tournament.id;
  const canonicalPath = `/tournaments/${tournament.slug || tournament.id}`;
  const format = tournament.format;
  const startIso = toIsoDate(tournament.startDate);
  const endIso = toIsoDate(tournament.endDate);
  const tDescription = truncate(
    tournament.description ||
      `Competitive KZ tournament — ${tournament.name || 'KZCOMP event'}. Brackets, leaderboards and live results on KZCOMP.`,
    160
  );
  const eventLd = {
    '@context': 'https://schema.org',
    '@type': 'SportsEvent',
    name: tournament.name || 'KZ Tournament',
    description: tDescription,
    url: absoluteUrl(canonicalPath),
    sport: 'Kreedz Climbing (KZ)',
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    ...(startIso ? { startDate: startIso } : {}),
    ...(endIso ? { endDate: endIso } : {}),
    location: {
      '@type': 'VirtualLocation',
      url: absoluteUrl(canonicalPath),
    },
    organizer: { '@type': 'Organization', name: 'KZCOMP', url: absoluteUrl('/') },
    ...(tournament.coverImage ? { image: tournament.coverImage } : {}),
  };

  return (
    <div>
      <SEO
        title={tournament.name || 'KZ Tournament'}
        description={tDescription}
        type="article"
        canonicalPath={canonicalPath}
        image={tournament.coverImage}
        jsonLd={eventLd}
      />
      <TournamentHeader tournament={tournament} />
      {(tournament.format !== 'staged' || isSectionVisible(tournament, 'support')) && (
        <TournamentSupportSection tournament={tournament} />
      )}

      <div className="mx-auto max-w-screen-2xl px-4 py-10 sm:px-6 lg:py-14">
        {format === 'guess' && <AnonymousPlayerGuessingGame tournamentId={tournamentId} />}

        {format === 'rules-only' && <RulesOnlyTournamentView tournament={tournament} />}

        {format === 'mapping' && (
          <MappingTournamentView tournament={tournament} tournamentId={tournamentId} />
        )}

        {format === 'team-cumulative' && (
          <TeamCumulativeStandingsView tournament={tournament} tournamentId={tournamentId} />
        )}

        {format === 'external' && (
          <ExternalTournamentView
            tournament={tournament}
            players={players}
            tournamentId={tournamentId}
          />
        )}

        {format === 'bracket-external' && (
          <BracketExternalTournamentView
            tournament={tournament}
            players={players}
            tournamentId={tournamentId}
          />
        )}

        {format === 'leaderboard' && (
          <LeaderboardTournamentView
            tournament={tournament}
            players={players}
            tournamentId={tournamentId}
          />
        )}

        {format === 'team' && (
          <TeamTournamentView
            tournament={tournament}
            teams={teams}
            rounds={rounds}
            players={players}
            tournamentId={tournamentId}
          />
        )}

        {format === 'bracket' && (
          <BracketTournamentView
            tournament={tournament}
            players={players}
            tournamentId={tournamentId}
          />
        )}

        {format === 'groups-final' && (
          <GroupsFinalTournamentView
            tournament={tournament}
            players={players}
            tournamentId={tournamentId}
          />
        )}

        {format === 'staged' && (
          <StagedTournamentView
            tournament={tournament}
            players={players}
            tournamentId={tournamentId}
          />
        )}

        {!format && (
          <LeaderboardTournamentView
            tournament={tournament}
            players={players}
            tournamentId={tournamentId}
          />
        )}
      </div>
    </div>
  );
};

export default TournamentDetails;
